// src/shared/confirm-dialog.js
//
// v134.2 - a Shift "are you sure?" box (the 15 Oct 2026 look-and-feel review). Shift used the browser's own confirm()
// about 65 times. Those boxes look different in every browser, cannot mark an action as destructive, say "OK" for
// everything, and on a till they appear over the page with the site's address in the title. This is the replacement:
// a real dialog (role, aria-modal, Escape cancels, focus kept inside, starting on Cancel for a destructive action),
// the action named on its button, and a red button when it cannot be undone.
//
// uiConfirm(message, opts) -> Promise<boolean>
//   message   the question; a first line, then any detail after a blank line (plain text, never HTML)
//   opts.confirmLabel  the button's words (default "Continue"; "Delete" etc. for destructive actions)
//   opts.danger        a red button, and focus starts on Cancel
//   opts.checkbox      v136.9: { label, checked } shows one tick box above the buttons (unticked unless checked is true);
//                      when the dialog closes, opts.checkbox.checked is set to whether it was ticked. The answer is still
//                      the boolean (Cancel ignores the box). Used by Clear Week's "Also clear booked leave".
//
// Exposed as window.uiConfirm too, so inline handlers and classic scripts can use it.

export function uiConfirm(message, opts) {
  const o = opts || {};
  return new Promise(function (resolve) {
    if (typeof document === 'undefined') { resolve(false); return; }
    const text = String(message == null ? '' : message);
    const cut = text.indexOf('\n');
    const head = cut === -1 ? text : text.slice(0, cut);
    const rest = cut === -1 ? '' : text.slice(cut + 1).replace(/^\n+/, '');

    const back = document.createElement('div');
    back.setAttribute('style', 'position:fixed;inset:0;z-index:100000;background:rgba(17,24,20,.55);display:flex;align-items:center;justify-content:center;padding:16px');
    const box = document.createElement('div');
    box.setAttribute('role', 'alertdialog');
    box.setAttribute('aria-modal', 'true');
    box.setAttribute('aria-labelledby', 'uiConfirmTitle');
    if (rest) box.setAttribute('aria-describedby', 'uiConfirmBody');
    box.setAttribute('style', 'background:var(--card);color:var(--text);border-radius:14px;max-width:440px;width:100%;padding:20px;box-shadow:0 16px 44px rgba(17,33,25,.24);font-family:inherit');

    const h = document.createElement('div');
    h.id = 'uiConfirmTitle';
    h.setAttribute('style', 'font-size:17px;font-weight:700;line-height:1.35');
    h.textContent = head;
    box.appendChild(h);
    if (rest) {
      const b = document.createElement('div');
      b.id = 'uiConfirmBody';
      b.setAttribute('style', 'font-size:14px;color:var(--tiny);margin-top:8px;white-space:pre-line;line-height:1.5');
      b.textContent = rest;
      box.appendChild(b);
    }

    // v136.9: the optional tick box (opts.checkbox), a real labelled checkbox so a tap on the words ticks it.
    let tick = null;
    if (o.checkbox && o.checkbox.label) {
      const lab = document.createElement('label');
      lab.setAttribute('style', 'display:flex;align-items:flex-start;gap:10px;margin-top:14px;font-size:14px;line-height:1.4;cursor:pointer');
      tick = document.createElement('input');
      tick.type = 'checkbox';
      tick.checked = o.checkbox.checked === true;
      tick.setAttribute('style', 'width:20px;height:20px;margin:0;flex:none;cursor:pointer');
      const words = document.createElement('span');
      words.textContent = String(o.checkbox.label);
      lab.appendChild(tick);
      lab.appendChild(words);
      box.appendChild(lab);
    }

    const btns = document.createElement('div');
    btns.setAttribute('style', 'display:flex;gap:10px;justify-content:flex-end;flex-wrap:wrap;margin-top:18px');
    const cancel = document.createElement('button');
    cancel.type = 'button';
    cancel.textContent = o.cancelLabel || 'Cancel';
    cancel.setAttribute('style', 'min-height:44px;padding:0 18px;border:1.5px solid var(--border-strong);background:var(--card);color:var(--text);border-radius:10px;font-size:15px;cursor:pointer');
    const ok = document.createElement('button');
    ok.type = 'button';
    ok.textContent = o.confirmLabel || 'Continue';
    ok.setAttribute('style', 'min-height:44px;padding:0 20px;border:none;color:#fff;border-radius:10px;font-size:15px;font-weight:700;cursor:pointer;background:' + (o.danger ? '#d92d20' : '#007A2F'));
    btns.appendChild(cancel);
    btns.appendChild(ok);
    box.appendChild(btns);
    back.appendChild(box);

    const before = document.activeElement;
    function close(v) {
      document.removeEventListener('keydown', onKey, true);
      if (tick) { try { o.checkbox.checked = tick.checked === true; } catch (e) { /* a frozen options object */ } }
      if (back.parentNode) back.parentNode.removeChild(back);
      try { if (before && before.focus) before.focus(); } catch (e) { /* the opener may be gone */ }
      resolve(v);
    }
    function onKey(e) {
      if (e.key === 'Escape') { e.preventDefault(); close(false); }
      else if (e.key === 'Tab') {
        e.preventDefault();
        // v136.9: focus stays inside, round the tick box too when there is one (two buttons: exactly as before).
        const ring = tick ? [tick, cancel, ok] : [cancel, ok];
        const at = ring.indexOf(document.activeElement);
        const step = e.shiftKey ? ring.length - 1 : 1;
        ring[at === -1 ? 0 : (at + step) % ring.length].focus();
      }
    }
    cancel.onclick = function () { close(false); };
    ok.onclick = function () { close(true); };
    back.addEventListener('mousedown', function (e) { if (e.target === back) close(false); });
    document.addEventListener('keydown', onKey, true);
    document.body.appendChild(back);
    setTimeout(function () { (o.danger ? cancel : ok).focus(); }, 30);
  });
}

if (typeof window !== 'undefined' && !window.uiConfirm) window.uiConfirm = uiConfirm;
